<script setup>
const { loggedIn, clear } = useUserSession()
const { locales, locale, setLocale } = useI18n()
const { $notify, $prompt } = useNuxtApp()
const showSidebar = ref(false)
const showLanguageSidebar = ref(false)
const { Settings } = useSettings()
const customURLDialog = ref(null)

useHead({
  link: [
    { rel: 'stylesheet', href: '/custom_css', defer: true, async: true }
  ]
})

const menuItems = [
  { title: 'About', path: '/about', icon: 'mdi-help-circle-outline' },
  { title: 'Blobs', path: '/blobs', icon: 'mdi-book-multiple-outline' },
  { title: 'Tags', path: '/tags', icon: 'mdi-tag-multiple-outline' },
  { title: 'Share', path: '/share', icon: 'mdi-widgets-outline' },
]
if (Settings.value.enableSearch) {
  menuItems.push(
      { title: 'Search', path: '/search', icon: 'mdi-magnify' }
      )
}

async function logout() {
  await clear()
  navigateTo('/')
}

const isValidUrl = urlString => /^https?:\/\/.+\..+/.test(urlString)
async function onAddCustomPost() {
  const url = await $prompt('Add post')
  if (isValidUrl(url)) {
    customURLDialog.value.open(url)
  } else {
    $notify('Invalid URL', 'error')
  }
}
async function customURLAdded() {
  console.debug('ok')
}
</script>

<template>
<v-app>
  <client-only>
    <Dialog />
    <Notification />
  </client-only>
  <v-navigation-drawer
    id="blob_sidebar"
    v-model="showSidebar"
    temporary
  >
    <v-list nav>
      <v-list-item
        v-for="item in menuItems"
        :key="$t(item.title)"
        :class="item.title"
        :to="item.path"
        :prepend-icon="item.icon"
      >
        <span class="label">{{ item.title }}</span>
      </v-list-item>
      <v-list-item
        v-if="!loggedIn"
        to="/signin"
        prepend-icon="mdi-lock-open-outline"
      >
        <span class="label">Sign in</span>
      </v-list-item>
      <v-list-item
        v-if="loggedIn"
        to="/admin"
        prepend-icon="mdi-cog"
      >
        <span class="label">Admin</span>
      </v-list-item>
      <v-list-item
        v-if="loggedIn"
        to="/signin"
        prepend-icon="mdi-logout"
        @click="logout"
      >
        <span class="label">{{ $t('Signout') }}</span>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>

  <v-navigation-drawer
    v-model="showLanguageSidebar"
    location="right"
    temporary
  >
    <v-card-title>{{ $t('Language') }}</v-card-title>
    <v-list nav>
      <v-list-item
        v-for="item in locales"
        :key="item.code"
        :active="locale === item.code"
        @click="setLocale(item.code)"
      >
        {{ item.name }}
      </v-list-item>
    </v-list>
  </v-navigation-drawer>

  <v-app-bar
    id="blob_appbar"
    class="text-primary"
    elevation="0"
  >
    <v-app-bar-nav-icon
      class="d-flex d-sm-none"
      @click="showSidebar = !showSidebar"
    />
    <v-toolbar-title>
      <nuxt-link
        class="text-decoration-none"
        to="/"
      >
        <img
          id="blob_logo"
          :src="'/media/logo'"
          :alt="Settings.name"
        >
      </nuxt-link>
    </v-toolbar-title>
    <v-toolbar-items>
      <client-only>
        <CustomURLDialog
          ref="customURLDialog"
          @added="customURLAdded"
        />
      </client-only>
      <v-btn
        v-for="item in menuItems"
        :key="item.path"
        class="d-none d-sm-flex"
        :class="item.title"
        :to="item.path"
      >
        <v-icon>{{ item.icon }}</v-icon>
        <span class="label">{{ $t(item.title) }}</span>
      </v-btn>
      <v-btn
        class="Translate"
        @click="showLanguageSidebar = !showLanguageSidebar"
      >
        <v-icon>mdi-translate</v-icon>
      </v-btn>
      <client-only
        v-if="loggedIn"
      >
        <v-menu
          open-on-hover
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              class="d-none d-sm-block"
            >
              <v-icon>mdi-menu</v-icon>
            </v-btn>
          </template>
          <v-card
            width="250"
            rounded="0"
          >
            <v-card-text>
              <v-btn
                to="/admin"
                color="primary"
                block
                class="mb-3"
              >
                {{ $t('Admin') }}
              </v-btn>
              <v-btn
              color="secondary"
              block
              class="mb-3"
              @click="onAddCustomPost()"
              >
                {{ $t('post.add_custom') }}
              </v-btn>
              <v-btn
                block
                color="primary"
                @click="logout"
              >
                {{ $t('Signout') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-menu>
        <template #placeholder>
          <v-btn icon="mdi-menu" />
        </template>
      </client-only>
      <v-btn
        v-else
        to="/signin"
        class="d-none d-sm-flex"
      >
        <v-icon>mdi-lock-open-outline</v-icon>
      </v-btn>
    </v-toolbar-items>
  </v-app-bar>

  <v-main id="blob_main">
    <a
      id="content"
      name="content"
    />
    <v-container :class="[`theme-${Settings?.theme ?? 'Grid'}`]">
      <NuxtPage />
    </v-container>
  </v-main>
</v-app>
</template>

<style>
.page-enter-active,.page-leave-active {  transition: all 0.4s;}.page-enter-from,.page-leave-to {  opacity: 0;  filter: blur(1rem);}

#blob_logo {
  height: 50px;
}

#blob_main {
  padding-top: 68px;
  background-color: #fafafa;
}
</style>
